import { useEffect, useState } from "react";
import type { PresetPropertyIcon, Property, PropertyType } from "./api.js";
import { cn } from "../ui/cn.js";

/**
 * Small rounded-square property icon: the customer's photo/aerial image when
 * a presigned `iconUrl` is present, else their chosen preset glyph, else a
 * default glyph keyed off the property type. Presets are emoji so they render
 * client-side with no URL round trip (the app's SVG set in ui/icons.tsx is
 * deliberately tiny — see PRESET_PROPERTY_ICONS in api.ts).
 */
export const presetIconGlyphs: Record<PresetPropertyIcon, string> = {
  house: "🏠",
  apartment: "🏢",
  condo: "🏙️",
  townhouse: "🏘️",
  cabin: "🛖",
  building: "🏛️",
  yard: "🌳",
  farm: "🚜",
  workshop: "🛠️",
  storefront: "🏪",
};

export const presetIconLabels: Record<PresetPropertyIcon, string> = {
  house: "House",
  apartment: "Apartment",
  condo: "Condo",
  townhouse: "Townhouse",
  cabin: "Cabin",
  building: "Building",
  yard: "Yard",
  farm: "Farm",
  workshop: "Workshop",
  storefront: "Storefront",
};

/** Fallback preset when the customer hasn't picked an icon. */
const defaultPresetByType: Record<PropertyType, PresetPropertyIcon> = {
  single_family: "house",
  multi_family: "apartment",
  condo: "condo",
  townhouse: "townhouse",
  commercial: "storefront",
  other: "building",
};

export function PropertyIconBadge({
  property,
  className,
}: {
  property: Pick<Property, "icon" | "iconUrl" | "propertyType">;
  className?: string;
}) {
  // Presigned URLs expire (~5 min) — a dead URL falls back to the glyph
  // instead of a broken image. Reset when a fresh URL arrives.
  const [imgFailed, setImgFailed] = useState(false);
  const url = property.iconUrl;
  useEffect(() => setImgFailed(false), [url]);
  if (url && !imgFailed) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setImgFailed(true)}
        className={cn("shrink-0 rounded-xl object-cover", className)}
      />
    );
  }
  // Photo/aerial icons without a resolved URL (e.g. the aerial crop isn't
  // stored yet) fall back to the type default, same as no icon at all.
  const preset =
    property.icon?.kind === "preset"
      ? property.icon.value
      : defaultPresetByType[property.propertyType];
  return (
    <span
      aria-hidden="true"
      className={cn("flex shrink-0 items-center justify-center rounded-xl bg-brand-50", className)}
    >
      <span className="text-2xl leading-none">{presetIconGlyphs[preset]}</span>
    </span>
  );
}
